import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
    Plus, Trash2, Copy, ArrowUp, ArrowDown, Upload, X,
    Type as TypeIcon, Heading2, Quote, List as ListIcon, Image as ImageIcon,
    PenLine, Bold, Link as LinkIcon, Eye,
} from "lucide-react";
import { emptyBlock, blocksToContent } from "@/lib/blogBlocks";
import { renderContent } from "@/lib/blogRender";

/**
 * Structured block editor for editorial content (Blog + Nota de Obra).
 *
 * Contract: `value` is an array of blocks; `onChange(newBlocks)` fires on
 * every mutation. The parent is expected to keep the mirror `content`
 * string derived via `blocksToContent(newBlocks)` in sync so the existing
 * save/send flow continues to validate on `content.trim()`.
 *
 * The `types` prop filters which block types the type-picker exposes. By
 * default the blog set is used (no signature). Nota de Obra passes an
 * extended list that adds the signature block. The `renderPreview` prop
 * customises what the Preview modal shows — for emails, callers pass a
 * function that fetches the server-rendered HTML so admins see the exact
 * template that will land in inboxes.
 *
 * Design intent (from product):
 *   - No markdown chars are ever displayed to the user.
 *   - No manual font/color/size controls — the template owns visuals.
 *   - Inline formatting is limited to Bold and Link on paragraph/list items.
 */

const BLOCK_META = {
    paragraph: { label: "Parágrafo", Icon: TypeIcon },
    subtitle:  { label: "Subtítulo", Icon: Heading2 },
    pull_quote:{ label: "Destaque",  Icon: Quote },
    list:      { label: "Lista",     Icon: ListIcon },
    image:     { label: "Imagem",    Icon: ImageIcon },
    signature: { label: "Assinatura", Icon: PenLine },
};

const DEFAULT_TYPES = ["paragraph", "subtitle", "list", "pull_quote", "image"];

export default function BlogBlockEditor({ value, onChange, types = DEFAULT_TYPES, renderPreview }) {
    const blocks = Array.isArray(value) && value.length ? value : [emptyBlock("paragraph")];
    const [preview, setPreview] = useState(false);
    const [addingAt, setAddingAt] = useState(null); // index or "end"

    const set = useCallback((newBlocks) => onChange(newBlocks), [onChange]);

    const patch = (idx, changes) => {
        const next = blocks.map((b, i) => (i === idx ? { ...b, ...changes } : b));
        set(next);
    };

    const addAt = (idx, type) => {
        const next = [...blocks];
        next.splice(idx, 0, emptyBlock(type));
        set(next);
        setAddingAt(null);
    };

    const move = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= blocks.length) return;
        const next = [...blocks];
        [next[idx], next[target]] = [next[target], next[idx]];
        set(next);
    };

    const duplicate = (idx) => {
        const src = blocks[idx];
        const next = [...blocks];
        next.splice(idx + 1, 0, { ...src, id: `${src.id}-copy-${Date.now().toString(36)}` });
        set(next);
    };

    const remove = (idx) => {
        if (blocks.length === 1) {
            // Never leave the editor empty — reset the single remaining block instead.
            set([emptyBlock("paragraph")]);
            return;
        }
        const next = blocks.filter((_, i) => i !== idx);
        set(next);
    };

    return (
        <div className="space-y-3" data-testid="blog-block-editor">
            <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-stone-500">
                    <span className="text-stone-400">{blocks.length} bloco{blocks.length === 1 ? "" : "s"}</span>
                </span>
                <button type="button" onClick={() => setPreview(true)}
                    className="text-xs px-3 py-1.5 border border-stone-300 rounded flex items-center gap-1.5"
                    data-testid="blog-preview-btn">
                    <Eye className="w-3.5 h-3.5" /> Pré-visualizar
                </button>
            </div>

            {blocks.map((b, i) => (
                <div key={b.id} className="group border border-stone-200 rounded-lg" data-testid={`block-${i}`}>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-stone-50 rounded-t-lg text-[11px] uppercase tracking-widest text-stone-500 font-mono border-b border-stone-200">
                        <BlockChip type={b.type} />
                        <div className="flex-1" />
                        <IconBtn title="Subir" onClick={() => move(i, -1)} disabled={i === 0} testid={`block-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></IconBtn>
                        <IconBtn title="Descer" onClick={() => move(i, +1)} disabled={i === blocks.length - 1} testid={`block-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></IconBtn>
                        <IconBtn title="Duplicar" onClick={() => duplicate(i)} testid={`block-dup-${i}`}><Copy className="w-3.5 h-3.5" /></IconBtn>
                        <IconBtn title="Apagar" onClick={() => remove(i)} danger testid={`block-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                    </div>
                    <div className="p-3">
                        <BlockBody block={b} onChange={(changes) => patch(i, changes)} />
                    </div>
                    <div className="px-3 pb-2 opacity-0 group-hover:opacity-100 transition">
                        <AddBlockRow index={i + 1} types={types} onAdd={(t) => addAt(i + 1, t)}
                            open={addingAt === i + 1} onToggle={() => setAddingAt(addingAt === i + 1 ? null : i + 1)} />
                    </div>
                </div>
            ))}

            <AddBlockRow index="end" types={types} onAdd={(t) => addAt(blocks.length, t)}
                open={addingAt === "end"} onToggle={() => setAddingAt(addingAt === "end" ? null : "end")} />

            {preview && <PreviewModal blocks={blocks} renderPreview={renderPreview} onClose={() => setPreview(false)} />}
        </div>
    );
}

// ─── Body rendering per block type ──────────────────────────────────

function BlockBody({ block, onChange }) {
    if (block.type === "paragraph")  return <TextBody block={block} onChange={onChange} inlineTools placeholder="Escreva o parágrafo…" testid="body-paragraph" />;
    if (block.type === "subtitle")   return <TextBody block={block} onChange={onChange} placeholder="Subtítulo (H2)" bold testid="body-subtitle" />;
    if (block.type === "pull_quote") return <TextBody block={block} onChange={onChange} placeholder="Frase de destaque com barra cor de cobre" italicHint testid="body-pull-quote" />;
    if (block.type === "signature")  return <TextBody block={block} onChange={onChange} placeholder={"Cumprimentos,\nA Equipa O Homem da Obra"} italicHint testid="body-signature" />;
    if (block.type === "list")       return <ListBody block={block} onChange={onChange} />;
    if (block.type === "image")      return <ImageBody block={block} onChange={onChange} />;
    return null;
}

/* Simple textarea with an inline-formatting toolbar. The stored value is
   markdown-lite (`**bold**` and `[label](url)`) — the user never types those
   markers because the toolbar buttons wrap the current selection. */
function TextBody({ block, onChange, inlineTools = false, bold = false, italicHint = false, placeholder = "", testid }) {
    const ref = useRef(null);

    const wrapSelection = (before, after = before) => {
        const el = ref.current;
        if (!el) return;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const val = el.value;
        const selected = val.slice(start, end);
        if (start === end) {
            // No selection → insert marker pair with the caret between them.
            const insert = `${before}${after}`;
            el.value = val.slice(0, start) + insert + val.slice(end);
            onChange({ text: el.value });
            requestAnimationFrame(() => {
                el.focus();
                const pos = start + before.length;
                el.setSelectionRange(pos, pos);
            });
            return;
        }
        el.value = val.slice(0, start) + before + selected + after + val.slice(end);
        onChange({ text: el.value });
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + before.length, start + before.length + selected.length);
        });
    };

    const insertLink = () => {
        const el = ref.current;
        if (!el) return;
        const url = window.prompt("URL do link (ex.: https://exemplo.com ou /orcamento)");
        if (!url) return;
        const start = el.selectionStart, end = el.selectionEnd;
        const selected = el.value.slice(start, end) || "texto do link";
        const inserted = `[${selected}](${url})`;
        el.value = el.value.slice(0, start) + inserted + el.value.slice(end);
        onChange({ text: el.value });
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + 1, start + 1 + selected.length);
        });
    };

    return (
        <div>
            {inlineTools && (
                <div className="flex gap-1 mb-2" data-testid={`${testid}-tools`}>
                    <ToolBtn onClick={() => wrapSelection("**")} title="Negrito (Ctrl+B)" testid={`${testid}-bold`}><Bold className="w-3.5 h-3.5" /></ToolBtn>
                    <ToolBtn onClick={insertLink} title="Inserir link" testid={`${testid}-link`}><LinkIcon className="w-3.5 h-3.5" /></ToolBtn>
                </div>
            )}
            <textarea
                ref={ref}
                value={block.text || ""}
                onChange={(e) => onChange({ text: e.target.value })}
                onKeyDown={(e) => {
                    if (inlineTools && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") {
                        e.preventDefault();
                        wrapSelection("**");
                    }
                }}
                rows={block.type === "subtitle" ? 1 : (block.type === "pull_quote" ? 3 : 5)}
                placeholder={placeholder}
                className={`w-full border border-stone-200 rounded p-2 text-sm ${bold ? "font-semibold" : ""} ${italicHint ? "italic" : ""}`}
                data-testid={testid}
            />
            {italicHint && (
                <p className="mt-1 text-[11px] text-stone-500">Aparece no artigo com barra cor de cobre à esquerda.</p>
            )}
        </div>
    );
}

function ListBody({ block, onChange }) {
    const items = block.items || [""];
    const patchItem = (i, v) => {
        const next = items.map((it, idx) => (idx === i ? v : it));
        onChange({ items: next });
    };
    const addItem = () => onChange({ items: [...items, ""] });
    const removeItem = (i) => {
        if (items.length === 1) return onChange({ items: [""] });
        onChange({ items: items.filter((_, idx) => idx !== i) });
    };
    return (
        <div className="space-y-2" data-testid="body-list">
            {items.map((it, i) => (
                <div key={i} className="flex items-center gap-2">
                    <span className="text-stone-400 text-sm">•</span>
                    <input
                        value={it}
                        onChange={(e) => patchItem(i, e.target.value)}
                        placeholder={`Item ${i + 1}`}
                        className="flex-1 border border-stone-200 rounded p-2 text-sm"
                        data-testid={`list-item-${i}`}
                    />
                    <button type="button" onClick={() => removeItem(i)} className="text-stone-400 hover:text-red-600" title="Remover item" data-testid={`list-item-del-${i}`}>
                        <X className="w-4 h-4" />
                    </button>
                </div>
            ))}
            <button type="button" onClick={addItem} className="text-xs text-[hsl(var(--copper))] flex items-center gap-1" data-testid="list-add-item">
                <Plus className="w-3 h-3" /> Adicionar item
            </button>
        </div>
    );
}

function ImageBody({ block, onChange }) {
    const [uploading, setUploading] = useState(false);
    const currentSrc = block.image_id ? fileUrl(block.image_id) : (block.image_url || "");
    const doUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd);
            onChange({ image_id: data.id, image_url: "" });
        } catch { toast.error("Falha no upload"); }
        finally { setUploading(false); }
    };
    return (
        <div className="space-y-2" data-testid="body-image">
            {currentSrc ? (
                <div className="relative w-full max-w-lg">
                    <img src={currentSrc} alt={block.image_alt || ""} className="w-full aspect-[16/9] object-cover rounded" />
                    <button type="button" onClick={() => onChange({ image_id: "", image_url: "" })}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow"
                        data-testid="image-clear">
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ) : (
                <label className="flex flex-col items-center justify-center w-full max-w-lg aspect-[16/9] border-2 border-dashed border-stone-300 rounded cursor-pointer hover:border-[hsl(var(--copper))] transition bg-stone-50" data-testid="image-upload">
                    {uploading
                        ? <span className="text-xs text-stone-500">A carregar…</span>
                        : <><Upload className="w-5 h-5 text-stone-400 mb-2" /><span className="text-xs text-stone-500">Carregar imagem</span></>}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && doUpload(e.target.files[0])} />
                </label>
            )}
            <label className="block max-w-lg">
                <span className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1">Texto alternativo (ALT)</span>
                <input
                    value={block.image_alt || ""}
                    onChange={(e) => onChange({ image_alt: e.target.value })}
                    placeholder="Descreva a imagem (obrigatório para acessibilidade)"
                    maxLength={200}
                    className="w-full border border-stone-200 rounded p-2 text-sm"
                    data-testid="image-alt"
                />
            </label>
        </div>
    );
}

// ─── Add-block row (dropdown of types) ──────────────────────────────

function AddBlockRow({ index, onAdd, open, onToggle, types = DEFAULT_TYPES }) {
    const label = index === "end" ? "Adicionar bloco no fim" : "Adicionar bloco aqui";
    return (
        <div className="flex items-center gap-2" data-testid={`add-row-${index}`}>
            {open ? (
                <div className="flex flex-wrap gap-1.5 flex-1 py-2">
                    {types.map((t) => {
                        const meta = BLOCK_META[t];
                        if (!meta) return null;
                        const { Icon, label: l } = meta;
                        return (
                            <button key={t} type="button" onClick={() => onAdd(t)}
                                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-white border border-stone-300 rounded hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))] transition"
                                data-testid={`add-${index}-${t}`}>
                                <Icon className="w-3.5 h-3.5" /> {l}
                            </button>
                        );
                    })}
                    <button type="button" onClick={onToggle} className="text-xs px-2 py-1 text-stone-400" data-testid={`add-${index}-cancel`}>Cancelar</button>
                </div>
            ) : (
                <button type="button" onClick={onToggle}
                    className="w-full flex items-center justify-center gap-1.5 text-xs text-stone-500 hover:text-[hsl(var(--copper))] py-2 border border-dashed border-stone-200 rounded"
                    data-testid={`add-toggle-${index}`}>
                    <Plus className="w-3.5 h-3.5" /> {label}
                </button>
            )}
        </div>
    );
}

// ─── Chips & utility buttons ─────────────────────────────────────────

function BlockChip({ type }) {
    const meta = BLOCK_META[type] || { label: type, Icon: TypeIcon };
    const Icon = meta.Icon;
    return (
        <span className="inline-flex items-center gap-1.5 text-[10px] text-stone-600">
            <Icon className="w-3.5 h-3.5" /> {meta.label}
        </span>
    );
}

function IconBtn({ children, onClick, disabled, title, danger = false, testid }) {
    return (
        <button type="button" onClick={onClick} disabled={disabled} title={title} data-testid={testid}
            className={`p-1 rounded ${danger ? "hover:bg-red-50 hover:text-red-600" : "hover:bg-white hover:text-stone-900"} ${disabled ? "opacity-30 pointer-events-none" : "text-stone-500"}`}>
            {children}
        </button>
    );
}

function ToolBtn({ children, onClick, title, testid }) {
    return (
        <button type="button" onClick={onClick} title={title} data-testid={testid}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-stone-200 bg-stone-50 rounded hover:bg-white hover:border-stone-300 text-stone-700">
            {children}
        </button>
    );
}

// ─── Preview modal ──────────────────────────────────────────────────

function PreviewModal({ blocks, renderPreview, onClose }) {
    const content = useMemo(() => blocksToContent(blocks), [blocks]);
    // Custom preview renderer (e.g. server-side email HTML). When absent,
    // fall back to the in-app renderer used by the public Blog page.
    const [customHtml, setCustomHtml] = useState(null);
    const [loading, setLoading] = useState(!!renderPreview);
    useEffect(() => {
        if (!renderPreview) return;
        let cancelled = false;
        setLoading(true);
        Promise.resolve(renderPreview({ content, blocks }))
            .then((html) => { if (!cancelled) setCustomHtml(html || ""); })
            .catch(() => { if (!cancelled) setCustomHtml(""); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [content, blocks, renderPreview]);

    return (
        <div className="fixed inset-0 bg-stone-950/70 z-[60] overflow-y-auto" onClick={onClose} data-testid="blog-preview-modal">
            <div className="min-h-full flex items-start justify-center p-4">
                <div className="bg-white rounded-xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-6 py-3 border-b border-stone-100">
                        <h3 className="font-display font-bold text-sm text-stone-700">
                            Pré-visualização — <span className="text-stone-400">como vai aparecer aos destinatários</span>
                        </h3>
                        <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="blog-preview-close">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    {renderPreview ? (
                        loading
                            ? <div className="p-10 text-center text-sm text-stone-500">A gerar pré-visualização…</div>
                            : <iframe title="Preview" srcDoc={customHtml || ""} className="w-full min-h-[600px] bg-white" data-testid="blog-preview-iframe" />
                    ) : (
                        <div className="max-w-[700px] mx-auto px-6 sm:px-10 py-8" data-testid="blog-preview-body">
                            {renderContent(content)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
